import {
  useCallback,
  useRef,
  useState,
} from 'react';


export type SpeechRate =
  | 'slow'
  | 'normal';


interface SpeakOptions {
  staticUrl?: string | null;
  fallback?: boolean;
}


export interface SpeechSequenceItem {
  text: string;
  lang?: string;
  staticUrl?: string | null;
  fallback?: boolean;
}


const RATE_VALUES: Record<
  SpeechRate,
  number
> = {
  slow: 0.85,
  normal: 1,
};


export function useGermanSpeech() {
  const [
    isSpeaking,
    setIsSpeaking,
  ] = useState(false);

  const [
    rate,
    setRate,
  ] = useState<SpeechRate>('normal');

  const [
    progress,
    setProgress,
  ] = useState(0);

  const [
    currentText,
    setCurrentText,
  ] = useState<string>('');

  const utteranceRef =
    useRef<SpeechSynthesisUtterance | null>(
      null
    );

  const audioRef =
    useRef<HTMLAudioElement | null>(null);


  const sequenceRunRef =
    useRef(0);


  const stop = useCallback(() => {
    sequenceRunRef.current += 1;

    const audio = audioRef.current;

    if (audio) {
      audio.pause();

      try {
        audio.currentTime = 0;
      } catch {
        // Safari may reject currentTime
        // before metadata has loaded.
      }

      audioRef.current = null;
    }

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    utteranceRef.current = null;
    setIsSpeaking(false);
    setProgress(0);
    setCurrentText('');
  }, []);

const speak = useCallback(
    (
        text: string,
        lang = 'de-DE',
        options: SpeakOptions = {},
    ) => {
        const {
            staticUrl = null,
            fallback = true,
        } = options;


        const speakWithBrowser = () => {
            if (
                !fallback
                || !('speechSynthesis' in window)
            ) {
                setIsSpeaking(false);
                setProgress(0);
                setCurrentText('');
                return false;
            }

            window.speechSynthesis.cancel();

            const utterance =
                new SpeechSynthesisUtterance(text);

            utterance.lang = lang;
            utterance.rate = RATE_VALUES[rate];

            utterance.onstart = () => {
                setIsSpeaking(true);
                setProgress(0);
                setCurrentText(text);
            };

            utterance.onend = () => {
                setIsSpeaking(false);
                utteranceRef.current = null;
                setProgress(0);
                setCurrentText('');
            };

            utterance.onerror = () => {
                setIsSpeaking(false);
                utteranceRef.current = null;
                setProgress(0);
                setCurrentText('');
            };

            // Browser TTS progress follows real speech boundary events.
            // Some engines may emit fewer boundary events; in that case
            // we intentionally avoid fake timer-based progress.
            utterance.onboundary = (event) => {
                if (
                    event.name === 'word'
                    || event.name === 'sentence'
                ) {
                    const length = text.length;

                    if (length > 0) {
                        setProgress(
                            Math.min(
                                event.charIndex / length,
                                1,
                            ),
                        );
                    }
                }
            };

            utteranceRef.current = utterance;

            window.speechSynthesis.speak(
                utterance
            );

            return true;
        };


        stop();


        if (!staticUrl) {
            return speakWithBrowser();
        }


        const audio =
            new Audio(staticUrl);

        audio.preload = 'auto';

        audio.playbackRate =
            rate === 'slow'
                ? 0.88
                : 1;

        audioRef.current = audio;

        const updateProgress = () => {
            if (
                audioRef.current !== audio
                || audio.paused
            ) {
                return;
            }

            if (
                Number.isFinite(audio.duration)
                && audio.duration > 0
            ) {
                setProgress(
                    Math.min(
                        audio.currentTime
                        / audio.duration,
                        1,
                    ),
                );
            }

            requestAnimationFrame(updateProgress);
        };

        audio.onplaying = () => {
            setIsSpeaking(true);
            setProgress(0);
            setCurrentText(text);
            requestAnimationFrame(updateProgress);
        };

        audio.onended = () => {
            if (audioRef.current === audio) {
                audioRef.current = null;
            }

            setIsSpeaking(false);
            setProgress(0);
            setCurrentText('');
        };

        audio.onerror = () => {
            if (audioRef.current === audio) {
                audioRef.current = null;
            }

            setIsSpeaking(false);
            setProgress(0);
            setCurrentText('');
        };

        setIsSpeaking(true);

        void audio.play().catch(() => {
            if (audioRef.current === audio) {
                audioRef.current = null;
            }

            setIsSpeaking(false);
            setProgress(0);
            setCurrentText('');

            speakWithBrowser();
        });

        return true;
    },
    [
        rate,
        stop,
    ],
);



  const speakSequence = useCallback(
    (
      items: SpeechSequenceItem[],
    ) => {
      stop();

      if (items.length === 0) {
        return false;
      }

      const runId =
        sequenceRunRef.current;

      let index = 0;

      const isCurrent = () =>
        sequenceRunRef.current === runId;

      const clearState = () => {
        if (!isCurrent()) {
          return;
        }

        setIsSpeaking(false);
        setProgress(0);
        setCurrentText('');
      };

      const playNext = () => {
        if (!isCurrent()) {
          return;
        }

        const item = items[index];

        if (!item) {
          clearState();
          return;
        }

        const {
          text,
          lang = 'de-DE',
          staticUrl = null,
          fallback = true,
        } = item;

        const finishCurrent = () => {
          if (!isCurrent()) {
            return;
          }

          index += 1;

          if (index >= items.length) {
            clearState();
            return;
          }

          playNext();
        };

        const playBrowserFallback = () => {
          if (!isCurrent()) {
            return;
          }

          if (
            !fallback
            || !('speechSynthesis' in window)
          ) {
            finishCurrent();
            return;
          }

          const utterance =
            new SpeechSynthesisUtterance(text);

          utterance.lang = lang;
          utterance.rate =
            RATE_VALUES[rate];

          utterance.onstart = () => {
            if (!isCurrent()) {
              return;
            }

            setIsSpeaking(true);
            setProgress(0);
            setCurrentText(text);
          };

          utterance.onend = () => {
            if (
              utteranceRef.current
              === utterance
            ) {
              utteranceRef.current = null;
            }

            finishCurrent();
          };

          utterance.onerror = () => {
            if (
              utteranceRef.current
              === utterance
            ) {
              utteranceRef.current = null;
            }

            finishCurrent();
          };

          utteranceRef.current =
            utterance;

          window.speechSynthesis.speak(
            utterance
          );
        };

        if (!staticUrl) {
          playBrowserFallback();
          return;
        }

        const audio =
          new Audio(staticUrl);

        audio.preload = 'auto';

        audio.playbackRate =
          rate === 'slow'
            ? 0.88
            : 1;

        audioRef.current = audio;

        const updateProgress = () => {
          if (
            !isCurrent()
            || audioRef.current !== audio
            || audio.paused
          ) {
            return;
          }

          if (
            Number.isFinite(
              audio.duration
            )
            && audio.duration > 0
          ) {
            setProgress(
              Math.min(
                audio.currentTime
                / audio.duration,
                1,
              ),
            );
          }

          requestAnimationFrame(
            updateProgress
          );
        };

        audio.onplaying = () => {
          if (!isCurrent()) {
            audio.pause();
            return;
          }

          setIsSpeaking(true);
          setProgress(0);
          setCurrentText(text);

          requestAnimationFrame(
            updateProgress
          );
        };

        audio.onended = () => {
          if (
            audioRef.current === audio
          ) {
            audioRef.current = null;
          }

          finishCurrent();
        };

        audio.onerror = () => {
          if (
            audioRef.current === audio
          ) {
            audioRef.current = null;
          }

          playBrowserFallback();
        };

        setIsSpeaking(true);

        void audio.play().catch(() => {
          if (
            audioRef.current === audio
          ) {
            audioRef.current = null;
          }

          playBrowserFallback();
        });
      };

      playNext();

      return true;
    },
    [
      rate,
      stop,
    ],
  );


  const playStaticCue = useCallback(
    (
      staticUrl: string,
    ) => {
      stop();

      const runId =
        sequenceRunRef.current;

      const audio =
        new Audio(staticUrl);

      audio.preload = 'auto';
      audio.playbackRate = 1;

      audioRef.current = audio;

      const finish = () => {
        if (
          sequenceRunRef.current
          !== runId
        ) {
          return;
        }

        if (
          audioRef.current === audio
        ) {
          audioRef.current = null;
        }

        setIsSpeaking(false);
        setProgress(0);
        setCurrentText('');
      };

      audio.onplaying = () => {
        if (
          sequenceRunRef.current
          !== runId
        ) {
          audio.pause();
          return;
        }

        setIsSpeaking(true);
        setProgress(0);
        setCurrentText('');
      };

      audio.onended = finish;
      audio.onerror = finish;

      setIsSpeaking(true);

      void audio.play().catch(
        finish
      );

      return true;
    },
    [
      stop,
    ],
  );


  return {
    speak,
    speakSequence,
    playStaticCue,
    stop,
    isSpeaking,
    rate,
    setRate,
    progress,
    currentText,
};
}
